/**
 * Per-job readiness for EMTs (feature flag `emt_readiness_v3`, 2026-10-10, Part A1 and A2).
 * Pure: no Firebase, no React. Agreed with Derek (Trident EMS) on the Oct 9 call.
 *
 * ONE SOURCE OF TRUTH. A job's readiness score is `evaluateJobForCandidate(...).match.total`: the
 * same calculateJobMatch number the employer sees for this EMT on this job (the employer's
 * application view runs the same code on the server, functions/src/candidateEvaluationForEmployer.ts).
 * Nothing here computes a second score. "What would raise it" re-runs that same code on a copy of
 * the profile with one change made (simulateJobScore), so every promised gain is a real number.
 *
 * Per job (jobReadinessView):
 *  - meets: every required item is on the profile and its document is reviewed (the apply gate
 *    passes without the employer's "apply anyway" switch). Big thumbs up, "You're good to go for
 *    [Company]", the score never shown in red.
 *  - missing: a required item is not on the profile, or its document is not reviewed yet.
 *    Exclamation mark, "What's missing (N)", plus the score.
 *  - far-off jobs are hidden by the board (src/lib/jobs/farOffJobs.ts), unchanged.
 *
 * THE AVERAGE RULE (competitivenessFromJobs). "Profile competitiveness" is the plain average,
 * rounded to a whole number, of the per-job scores over the jobs the EMT could realistically apply
 * to. A job counts when ALL of these hold:
 *  1. it is on the Jobs board (open, active, not expired, not a test job: the caller passes the
 *     board's own population, loadCandidateJobs / listOpenJobsPaginated);
 *  2. it is not far off (isFarOffJob, the board's rule, with the same driver options);
 *  3. it is near: its state equals the EMT's address state, OR the EMT saved a travel distance
 *     (jobPreferences.maxTravelRadiusMiles) and the job's map point is within that many miles.
 * With fewer than 3 such jobs there is no number: "Not enough jobs near you yet to compare", and
 * the page shows the parts view. Labels: 90 or more "Strong", 75 to 89 "Competitive", under 75
 * "Building". Nothing is stored: the average is recomputed whenever the jobs or the profile change.
 */
import type { Job } from '../firestore/types';
import type { JobMatchScore, ReadinessProfile } from '../../types/readiness';
import { calculateJobMatch, type JobMatchOptions } from './calculateJobMatch';
import { calculateReadiness } from './calculateReadiness';
import {
  STATE_EMS_CERTIFICATION,
  buildStrengthenAccountPath,
  certificationMatchKey,
  displayCertificationName,
  formatCertificationName,
  normalizeStateCode,
  resolveCanonicalCertification,
  stateCertificationNote,
  certificationSatisfiesForJob,
  COMPACT_PRIVILEGE_LABEL,
} from '../constants/certifications';
import { certificationJobContext, getEmtCertificationEntries, getJobCertRequirements } from '../certificationMatching';
import { certificationCheckKey, certificationCheckName } from '../credentials/certificationChecks';
import {
  ACLS_PATTERNS,
  BLS_PATTERNS,
  NREMT_PATTERNS,
  PALS_PATTERNS,
  PHTLS_PATTERNS,
  STATE_LICENSE_PATTERNS,
  certCounts,
  certMatchesPattern,
  countingCertificationEntries,
  extractCertifications,
  isExpired,
  scoringClock,
  scoringToday,
} from './certUtils';
import { isFarOffJob, type FarOffOptions } from '../jobs/farOffJobs';
import { jobStateCode } from '../jobs/jobState';
import { getJobCoordinates, type LatLng } from '../jobs/jobPreferenceMatching';
import { haversineMiles } from '../profileCoordinates';
import { countLabel } from '../text/plural';
import { getYearsExperienceForReadiness } from '../experienceRange';
import {
  joinWords,
  jobScoreBreakdown,
  lowerBecauseText,
  milesText,
  partsMissingPoints,
  profileCoords,
  stateName,
  type JobScoreBreakdown,
} from './jobScoreBreakdown';
import { DISTANCE_FREE_MILES, emtWorkCityPoints, travelDistanceToReach } from './distanceScore';

export const EMT_READINESS_V3_FLAG = 'emt_readiness_v3';
export const EMT_READINESS_V3_FLAG_LABEL = 'EMT per-job readiness scores (v3)';
export const EMT_READINESS_V3_FLAG_DESCRIPTION =
  "EMTs see a readiness score on every job (the same number the employer sees), a big thumbs up when they meet every required item, what would raise each score, and an overall competitiveness number averaged over the jobs near them. Needs emt_readiness_v2 on. Off: the Oct 9 view. Employers' numbers are unchanged.";

/** Shown next to every per-job score. */
export const JOB_SCORE_EXPLANATION = "How well you match what this job lists, how far it is, and your overall profile.";
export const MIN_JOBS_FOR_AVERAGE = 3;
/** The card's heading when there are fewer than 3 jobs to compare with (the line under it explains the rule). */
export const NOT_ENOUGH_JOBS_TEXT = 'Not enough jobs to compare you with yet';

const FIND_TRAINING_PATH = '/find-training';
const ACCOUNT_EXPERIENCE_HREF = '/account#past-experience';
const ACCOUNT_SKILLS_HREF = '/account#skills';
const ACCOUNT_HREF = '/account';
const ACCOUNT_BIO_HREF = '/account#bio';

export function findTrainingPath(cert: string): string {
  const name = cert.trim();
  return `${FIND_TRAINING_PATH}?cert=${encodeURIComponent(resolveCanonicalCertification(name) ?? name)}`;
}

/* ------------------------------------------------------------------ grade */

export type CompetitivenessLabel = 'Strong' | 'Competitive' | 'Building';
export type CompetitivenessTone = 'strong' | 'competitive' | 'building';

/** 90+ Strong, 75 to 89 Competitive, under 75 Building. */
export function competitivenessLabel(average: number): { label: CompetitivenessLabel; tone: CompetitivenessTone } {
  if (average >= 90) return { label: 'Strong', tone: 'strong' };
  if (average >= 75) return { label: 'Competitive', tone: 'competitive' };
  return { label: 'Building', tone: 'building' };
}

/* ------------------------------------------------------------------ simulation */

export type ProfileChange =
  | { kind: 'cert'; name: string; state?: string | null; level?: string | null }
  | { kind: 'skill'; skills: string[] }
  /** EMS work reaching `years` in total, plus (optionally) roles of these kinds for variety. */
  | { kind: 'years'; years: number; serviceTypes?: string[] }
  | { kind: 'photo' }
  | { kind: 'bio' }
  /** A profile document of this category (driversLicense, ...) that passed review. */
  | { kind: 'document'; category: string }
  | { kind: 'public' }
  /** The profile saved today ("active in the last 30 days"). */
  | { kind: 'touch' }
  | { kind: 'relocate' }
  /** A travel distance in job preferences (2026-10-14: distance scores by miles). */
  | { kind: 'travel'; miles: number };

const MS_PER_YEAR = 365.25 * 86400000;

function skillsList(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (raw && typeof raw === 'object') return Object.keys(raw).filter((k) => (raw as Record<string, unknown>)[k] === true);
  return [];
}

/** A copy of the readiness profile with one change made. The original is never touched. */
export function applyProfileChange(p: ReadinessProfile, change: ProfileChange, now: Date = new Date()): ReadinessProfile {
  const professional = { ...(p.professional ?? {}) };
  switch (change.kind) {
    case 'cert': {
      // Added the way it would really count (the counting rule): with a document that passed review
      // and no expiry, so every promised gain is the score AFTER the document is reviewed.
      const certs = [...(professional.certifications ?? [])];
      const fileUrl = `sim://${change.name}/${change.state ?? ''}/${change.level ?? ''}`;
      certs.push({ id: `sim-${change.name}`, name: change.name, fileUrl, ...(change.state ? { state: change.state } : {}), ...(change.level ? { level: change.level } : {}) } as never);
      const key = certificationCheckKey(fileUrl);
      const checks = { ...((p.certificationChecks as Record<string, unknown> | undefined) ?? {}) };
      if (key) checks[key] = { status: 'under_review', fileUrl, certName: certificationCheckName(change.name) };
      return { ...p, professional: { ...professional, certifications: certs }, certificationChecks: checks as ReadinessProfile['certificationChecks'] };
    }
    case 'skill': {
      const have = skillsList(professional.skills);
      return { ...p, professional: { ...professional, skills: [...new Set([...have, ...change.skills])] } };
    }
    case 'years': {
      // The roles already on the profile stay (their kinds earn the variety bonus; before 2026-10-14
      // they were replaced, so a promise could leave out points the EMT keeps). Years are the sum of
      // each role's length (pastExperience.ts), so one role covering only the missing time is added,
      // plus a zero-length role for each kind of work asked for (variety, no extra years).
      const today = scoringToday(scoringClock(p, now));
      const todayMs = Date.parse(today);
      const past = [...((p.pastExperience ?? []) as Array<Record<string, unknown>>)];
      const derived = getYearsExperienceForReadiness({ ...p, professional: { ...professional, experience: undefined } } as never, today);
      const gap = change.years - derived;
      if (gap > 0) {
        const start = new Date(todayMs - (gap + 0.06) * MS_PER_YEAR).toISOString().slice(0, 10);
        past.push({ id: 'sim-years', employerName: 'sim', roleTitle: 'sim', startDate: start, currentlyWorking: true });
      }
      for (const st of change.serviceTypes ?? []) past.push({ id: `sim-kind-${st}`, employerName: 'sim', roleTitle: 'sim', serviceType: st, startDate: today, endDate: today });
      return { ...p, pastExperience: past as never, professional: { ...professional, experience: { years: Math.max(change.years, derived) } } };
    }
    case 'photo':
      return { ...p, photoURL: p.photoURL || 'sim://photo' };
    case 'bio':
      return { ...p, bio: (p.bio || '').trim().length > 20 ? p.bio : 'Simulated bio long enough to count as written.' };
    case 'document': {
      const downloadUrl = `sim://document/${change.category}`;
      const docs = [...(p.profileDocuments ?? []), { id: `sim-${change.category}`, category: change.category, fileName: change.category, downloadUrl }];
      const key = certificationCheckKey(downloadUrl);
      const checks = { ...((p.certificationChecks as Record<string, unknown> | undefined) ?? {}) };
      if (key) checks[key] = { status: 'under_review', fileUrl: downloadUrl };
      return { ...p, profileDocuments: docs, certificationChecks: checks as ReadinessProfile['certificationChecks'] };
    }
    case 'public':
      return { ...p, preferences: { ...(p.preferences ?? {}), publicProfile: true } };
    case 'touch':
      return { ...p, updatedAt: { toMillis: () => now.getTime() } };
    case 'relocate':
      return { ...p, professional: { ...professional, jobPreferences: { ...(professional.jobPreferences ?? {}), openToRelocation: true } } };
    case 'travel':
      return { ...p, professional: { ...professional, jobPreferences: { ...(professional.jobPreferences ?? {}), maxTravelRadiusMiles: change.miles } as never } };
  }
}

/** The job's score after `change`, with exactly the code that made the score. */
export function simulateJobScore(p: ReadinessProfile, job: Job, change: ProfileChange, opts: JobMatchOptions = {}, now: Date = new Date()): number {
  const next = applyProfileChange(p, change, now);
  return calculateJobMatch(next, job, calculateReadiness(next, now), { ...opts, now }).total;
}

/** A held credential entry (name, state, level) re-added as one that counts: "once it is reviewed". */
function heldChange(entry: { name: string; state?: string | null; level?: string | null }): ProfileChange {
  return { kind: 'cert', name: entry.name, state: entry.state ?? null, level: entry.level ?? null };
}

/* ------------------------------------------------------------------ per job */

/** What evaluateJobForCandidate returned (only the fields this module reads). */
export type EvaluationLike = {
  job: Job;
  match: JobMatchScore | null;
  missingOnProfile: string[];
  pendingReview: Array<{ certification: string; reason: string }>;
  canApply: boolean;
  applyAnyway: boolean;
};

export type ImprovementKind = 'required' | 'documents' | 'preferred' | 'experience' | 'skills' | 'location' | 'profile';
export type ImprovementAction = { kind: 'add' | 'upload' | 'training' | 'edit'; label: string; href: string };
export type ScoreImprovement = {
  id: string;
  kind: ImprovementKind;
  /** Short: "Add EVOC". */
  title: string;
  /** Plain words: why, and what it does to this job's score. */
  detail: string;
  /** This job's score after the change (null when the change does not move the number). */
  newScore: number | null;
  gain: number;
  /** The credential involved, for "how to get it" and Find training. */
  cert?: string;
  actions: ImprovementAction[];
  /** The one profile change this promise was worked out with (distance and overall-profile items). */
  change?: ProfileChange;
};

export type JobReadinessView = {
  status: 'meets' | 'missing';
  score: number | null;
  /** "You're good to go for MedEx Medical Transport" or "What's missing (2)". */
  headline: string;
  missingCount: number;
  explanation: string;
  /**
   * Missing required items first, then documents, preferred, experience, skills, distance (relocation)
   * and the overall profile (biggest gain first). Every gain is the real score after that change.
   */
  improvements: ScoreImprovement[];
  /** Score with every required item added (null when nothing required is missing). */
  scoreWithAllRequired: number | null;
  /** "Florida EMT certification on file. Florida doesn't require a current NREMT to keep it." */
  stateNotes: string[];
  /** How THIS score adds up, part by part (the parts minus any cap equal `score`). Null with no score. */
  breakdown: JobScoreBreakdown | null;
  /**
   * "You meet everything this job lists. ..." Only ever set together with `breakdown`, and below 100
   * it always says what the remaining points depend on. Null when it does not apply.
   */
  meetsText: string | null;
  /** Thumbs up and a score under 75: "You can apply. Your score is lower because of ...". */
  lowerBecause: string | null;
  /** The same, short, for one-line places (the phone apply bar): "lower because of distance and your overall profile". */
  lowerBecauseShort: string | null;
  /** Score under 100 and nothing on the profile would raise it: says so, and why. */
  nothingText: string | null;
};

/** The thumbs-up line appears under this score. */
export const LOWER_SCORE_LINE_BELOW = 75;
const ACCOUNT_DOCUMENTS_HREF = '/account#profile-documents';
const JOB_PREFERENCES_HREF = '/account#job-preferences';
const ADDRESS_HREF = '/account#location';

/** 15 skills across airway, cardiac and trauma: the Skills part of readiness at 100. */
const FULL_SKILL_SET = [
  'Intubation', 'Suction', 'CPAP/BiPAP', '12-Lead ECG', 'Cardiac Monitoring', 'Defibrillation', 'Trauma Assessment',
  'Hemorrhage Control', 'Tourniquet', 'Patient Assessment', 'IV/IO Access', 'Medication Administration', 'Vital Signs',
  'Splinting', 'Oxygen Therapy',
];
/** Kinds of EMS work that earn the variety bonus, as the experience form saves them. */
const VARIETY_KINDS: Array<{ serviceType: string; words: string; test: RegExp }> = [
  { serviceType: '911', words: '911', test: /911|emergency/i },
  { serviceType: 'IFT', words: 'interfacility', test: /ift|interfacility/i },
  { serviceType: 'Event', words: 'events', test: /event/i },
];
/** Courses offered only to an AEMT or Paramedic (or when the job lists them). */
const ADVANCED_ONLY_COURSES = new Set(['ACLS', 'PALS']);

/** An AEMT or Paramedic credential on the profile (National Registry, or a State EMS Certification at that level). */
export function isAdvancedProvider(certs: ReadonlyArray<{ name: string; level?: string | null }>): boolean {
  return certs.some((c) => {
    const key = certificationMatchKey(c.name);
    if (key === 'match:aemt' || key === 'match:paramedic') return true;
    return resolveCanonicalCertification(c.name) === STATE_EMS_CERTIFICATION && /aemt|advanced|paramedic/i.test(String(c.level ?? ''));
  });
}

const READINESS_CREDENTIALS: Array<{ patterns: RegExp[]; name: string; add: string; label: string }> = [
  { patterns: NREMT_PATTERNS, name: 'NREMT-B', add: 'Add your EMT certification', label: 'NREMT' },
  { patterns: STATE_LICENSE_PATTERNS, name: STATE_EMS_CERTIFICATION, add: 'Add your state EMS certification', label: 'state EMS certification' },
  { patterns: BLS_PATTERNS, name: 'BLS', add: 'Add BLS', label: 'BLS' },
  { patterns: ACLS_PATTERNS, name: 'ACLS', add: 'Add ACLS', label: 'ACLS' },
  { patterns: PALS_PATTERNS, name: 'PALS', add: 'Add PALS', label: 'PALS' },
  { patterns: PHTLS_PATTERNS, name: 'PHTLS', add: 'Add PHTLS', label: 'PHTLS' },
];

function certChange(required: string, job: Job): ProfileChange {
  const canonical = resolveCanonicalCertification(required);
  if (canonical === STATE_EMS_CERTIFICATION) return { kind: 'cert', name: STATE_EMS_CERTIFICATION, state: jobStateCode(job) };
  return { kind: 'cert', name: canonical ?? required.trim() };
}

function certActions(cert: string, jobId: string, uploadOnly = false): ImprovementAction[] {
  const add = { kind: uploadOnly ? 'upload' : 'add', label: uploadOnly ? 'Upload it' : 'I have it: add it', href: buildStrengthenAccountPath([cert], jobId || undefined) } as ImprovementAction;
  return uploadOnly ? [add] : [add, { kind: 'training', label: 'How to get it', href: findTrainingPath(cert) }];
}

function companyOf(job: Job): string {
  const c = (job as { company?: unknown; orgName?: unknown }).company ?? (job as { orgName?: unknown }).orgName;
  return typeof c === 'string' && c.trim() ? c.trim() : 'this employer';
}

const gainText = (score: number, newScore: number) =>
  newScore > score ? `Raises this job's score from ${score}% to ${newScore}%.` : '';

/**
 * Everything the job card and job page need for one job. `readinessProfile` is the one the
 * evaluation used (toReadinessProfile of the private profile).
 */
export function jobReadinessView(
  e: EvaluationLike,
  readinessProfile: ReadinessProfile | null,
  opts: JobMatchOptions = {},
  now: Date = new Date(),
): JobReadinessView {
  const job = e.job;
  const jobId = job.id || '';
  const score = e.match ? e.match.total : null;
  const meets = e.canApply && !e.applyAnyway;
  const missingCount = e.missingOnProfile.length + e.pendingReview.length;
  const improvements: ScoreImprovement[] = [];
  let scoreWithAllRequired: number | null = null;
  const sim = (change: ProfileChange) => (readinessProfile ? simulateJobScore(readinessProfile, job, change, opts, now) : null);

  const ctx = certificationJobContext(job);
  const held = getEmtCertificationEntries({ professional: readinessProfile?.professional } as never);
  // Required items on the profile that do not count yet (pending, rejected or expired), each with the
  // held entry that would count once its document is reviewed.
  const pendingHeld = e.pendingReview
    .map((p) => ({ p, entry: held.find((h) => certificationSatisfiesForJob(h.name, h.state, p.certification, ctx, h.level)) }))
    .filter((x): x is { p: (typeof e.pendingReview)[number]; entry: (typeof held)[number] } => Boolean(x.entry));
  const notMetCount = e.missingOnProfile.length + pendingHeld.length;

  // 1. Required items not on the profile.
  if ((e.missingOnProfile.length || pendingHeld.length) && readinessProfile && score != null) {
    let all = readinessProfile;
    for (const m of e.missingOnProfile) all = applyProfileChange(all, certChange(m, job), now);
    for (const pr of pendingHeld) all = applyProfileChange(all, heldChange(pr.entry), now);
    scoreWithAllRequired = calculateJobMatch(all, job, calculateReadiness(all, now), { ...opts, now }).total;
  }
  for (const m of e.missingOnProfile) {
    const name = displayCertificationName(m);
    const alone = sim(certChange(m, job));
    const several = notMetCount > 1;
    const detail = several
      ? `Required to apply. With every required item on your profile, this job's score goes to about ${scoreWithAllRequired ?? score}%.`
      : `Required to apply. ${score != null && alone != null ? gainText(score, alone) : ''}`.trim();
    improvements.push({
      id: `required:${certificationMatchKey(m)}`,
      kind: 'required',
      title: `Add ${name}`,
      detail,
      newScore: several ? scoreWithAllRequired : alone,
      gain: Math.max(0, ((several ? scoreWithAllRequired : alone) ?? 0) - (score ?? 0)),
      cert: name,
      actions: certActions(name, jobId),
    });
  }

  // 2. Required, held, but it does not count yet (document pending, rejected or expired). It counts,
  // and the cap comes off, once its document passes review (the counting rule).
  for (const p of e.pendingReview) {
    const entry = pendingHeld.find((x) => x.p === p)?.entry;
    const after = entry ? sim(heldChange(entry)) : null;
    const newScore = notMetCount > 1 ? scoreWithAllRequired : after;
    const expired = /^expired/i.test(p.reason);
    improvements.push({
      id: `documents:${certificationMatchKey(p.certification)}`,
      kind: 'documents',
      title: expired ? `Renew your ${p.certification}` : `Upload your ${p.certification} document`,
      detail: [
        `Required to apply: ${p.reason}.`,
        'It counts once its document passes our document review.',
        notMetCount > 1
          ? `With every required item counted, this job's score goes to about ${scoreWithAllRequired ?? score}%.`
          : score != null && after != null ? gainText(score, after) : '',
      ].filter(Boolean).join(' '),
      newScore,
      gain: Math.max(0, (newScore ?? 0) - (score ?? 0)),
      cert: p.certification,
      actions: certActions(p.certification, jobId, true),
    });
  }

  // 3. Preferred credentials that do not count: not held, or held but not reviewed / expired.
  const { preferred } = getJobCertRequirements(job as Parameters<typeof getJobCertRequirements>[0]);
  const counting = readinessProfile ? countingCertificationEntries(readinessProfile, scoringClock(readinessProfile, now)) : [];
  const seenPref = new Set<string>();
  for (const p of preferred) {
    const key = certificationMatchKey(p);
    if (!key || seenPref.has(key)) continue;
    seenPref.add(key);
    if (counting.some((h) => certificationSatisfiesForJob(h.name, h.state, p, ctx, h.level))) continue;
    const name = displayCertificationName(p);
    const heldEntry = held.find((h) => certificationSatisfiesForJob(h.name, h.state, p, ctx, h.level));
    const after = sim(heldEntry ? heldChange(heldEntry) : certChange(p, job));
    improvements.push({
      id: `preferred:${key}`,
      kind: 'preferred',
      title: heldEntry ? `Get your ${name} document reviewed` : `Add ${name}`,
      detail: `Preferred by ${companyOf(job)}.${heldEntry ? ' It is on your profile but does not count until its document passes review and while it is current.' : ''} ${score != null && after != null ? gainText(score, after) : ''}`.trim(),
      newScore: after,
      gain: Math.max(0, (after ?? 0) - (score ?? 0)),
      cert: name,
      actions: certActions(name, jobId, Boolean(heldEntry)),
    });
  }

  // 4. Experience the job asks for.
  const exp = e.match?.pillars.experience;
  if (exp?.applicable && exp.emtYears < exp.requiredYears) {
    const after = sim({ kind: 'years', years: exp.requiredYears });
    improvements.push({
      id: 'experience',
      kind: 'experience',
      title: 'Experience',
      detail: `This job asks for ${exp.requiredYears} year${exp.requiredYears === 1 ? '' : 's'} of EMS experience; your profile shows ${exp.emtYears}. If you have more than your profile shows, add it. ${score != null && after != null ? gainText(score, after) : ''}`.trim(),
      newScore: after,
      gain: Math.max(0, (after ?? 0) - (score ?? 0)),
      actions: [{ kind: 'edit', label: 'Update your experience', href: ACCOUNT_EXPERIENCE_HREF }],
    });
  }

  // 5. Skills the job lists that the profile does not.
  const missingSkills = e.match?.pillars.skills.applicable ? e.match.pillars.skills.missing : [];
  if (missingSkills.length) {
    const after = sim({ kind: 'skill', skills: missingSkills });
    improvements.push({
      id: 'skills',
      kind: 'skills',
      title: `Add ${missingSkills.length === 1 ? 'a skill' : 'skills'}: ${missingSkills.slice(0, 4).join(', ')}${missingSkills.length > 4 ? ` +${missingSkills.length - 4} more` : ''}`,
      detail: `${companyOf(job)} lists ${missingSkills.length === 1 ? 'this skill' : 'these skills'}. Add only what you can do. ${score != null && after != null ? gainText(score, after) : ''}`.trim(),
      newScore: after,
      gain: Math.max(0, (after ?? 0) - (score ?? 0)),
      actions: [{ kind: 'edit', label: 'Add skills', href: ACCOUNT_SKILLS_HREF }],
    });
  }

  // 6. Distance. Scored by miles when both map points exist (distanceScore.ts, 2026-10-14): a travel
  // distance that reaches this job brings the part to full points, so it is offered with its real gain
  // ("only if you would travel that far"). By state (no map point): only relocation can raise it.
  const loc = e.match?.pillars.location;
  if (loc?.applicable && loc.scoredBy === 'miles' && typeof loc.miles === 'number' && loc.score < 100 && score != null) {
    const need = travelDistanceToReach(loc.miles, loc.travelMiles ?? null);
    const after = need != null ? sim({ kind: 'travel', miles: need }) : null;
    if (need != null && after != null && after > score) {
      const set = loc.travelMiles != null;
      improvements.push({
        id: 'location:travel',
        kind: 'location',
        title: set ? `Raise your travel distance to ${need} miles` : 'Set a travel distance',
        detail: `This job is ${milesText(Math.max(1, loc.miles))} from you in a straight line. A travel distance of ${need} miles or more counts it as within your reach. Set it only if you would travel that far for work. ${gainText(score, after)}`,
        newScore: after,
        gain: after - score,
        actions: [{ kind: 'edit', label: set ? 'Change your travel distance' : 'Set a travel distance', href: JOB_PREFERENCES_HREF }],
        change: { kind: 'travel', miles: need },
      });
    }
  }
  if (loc?.applicable && loc.score < 70 && loc.jobAcceptsRelocation && !loc.emtOpenToRelocation) {
    const after = sim({ kind: 'relocate' });
    if (score != null && after != null && after > score) {
      improvements.push({
        id: 'location:relocate',
        kind: 'location',
        title: "Say you're open to relocating",
        detail: `This job is in ${stateName(loc.jobState)} and accepts people who relocate. Tick "I'm open to relocating" in your job preferences only if you would move for the right job. ${gainText(score, after)}`,
        newScore: after,
        gain: after - score,
        actions: [{ kind: 'edit', label: 'Job preferences', href: JOB_PREFERENCES_HREF }],
        change: { kind: 'relocate' },
      });
    }
  }

  // 7. The overall profile (readiness). Every change that would raise THIS job's score, each run
  // through the same scoring; only real gains are listed, biggest first.
  if (readinessProfile && score != null && e.match && e.match.pillars.readinessFloor.score < 100) {
    const profileItems = overallProfileImprovements(readinessProfile, job, improvements, score, sim, opts, now);
    improvements.push(...profileItems);
  }

  // State certifications standing in for NREMT on this job, in the agreed wording.
  const stateNotes = [
    ...new Set(
      (e.match?.pillars.certifications.satisfiedBy ?? [])
        .map((s) => {
          // EMS Compact: another Compact state's license counts here (B4).
          if (s.satisfiedBy.endsWith(`, ${COMPACT_PRIVILEGE_LABEL}`)) {
            return `${COMPACT_PRIVILEGE_LABEL}: your ${s.satisfiedBy.slice(0, -(COMPACT_PRIVILEGE_LABEL.length + 2))} counts for this job.`;
          }
          const entry = held.find((h) => formatCertificationName(h.name, h.state, h.level) === s.satisfiedBy);
          return entry && resolveCanonicalCertification(entry.name) === STATE_EMS_CERTIFICATION && certificationMatchKey(s.required) !== certificationMatchKey(STATE_EMS_CERTIFICATION)
            ? stateCertificationNote(entry.state, entry.level)
            : null;
        })
        .filter((x): x is string => Boolean(x)),
    ),
  ];

  // How the score adds up, and the words that go with it.
  const breakdown = e.match
    ? jobScoreBreakdown(e.match, { audience: 'emt', emtCoords: profileCoords(readinessProfile), jobCoords: getJobCoordinates(job) })
    : null;
  const short = breakdown ? partsMissingPoints(breakdown) : [];
  const shortNames = joinWords(short.map((p) => PART_NAMES[p.key]));
  let meetsText: string | null = null;
  if (breakdown && score != null && breakdown.listsAnything && breakdown.meetsEverythingListed) {
    meetsText = score >= 100
      ? 'You meet everything this job lists, and your score is 100%.'
      : `You meet everything this job lists. The other ${100 - score} points depend on ${shortNames || 'the parts above'}, shown above.`;
  } else if (breakdown && score != null && score >= 100) {
    meetsText = 'Your score for this job is 100%.';
  }
  const nothingText = breakdown && score != null && score < 100 && improvements.length === 0
    ? `Nothing you can change on your profile would raise this score right now. The ${100 - score} points still missing are for ${joinWords(short.map((p) => p.reason)) || 'the parts above'}.`
    : null;
  const lowerBecause = breakdown && meets && score != null && score < LOWER_SCORE_LINE_BELOW
    ? `You can apply. ${lowerBecauseText(breakdown) ?? ''}`.trim()
    : null;

  return {
    status: meets ? 'meets' : 'missing',
    score,
    headline: meets ? `You're good to go for ${companyOf(job)}` : missingCount > 0 ? `What's missing (${missingCount})` : "What's missing",
    missingCount,
    explanation: JOB_SCORE_EXPLANATION,
    improvements,
    scoreWithAllRequired,
    stateNotes,
    breakdown,
    meetsText,
    lowerBecause,
    lowerBecauseShort: lowerBecause && short.length ? `lower because of ${joinWords(short.slice(0, 2).map((p) => PART_NAMES[p.key]))}` : null,
    nothingText,
  };
}

/** How a part is named inside a sentence ("depend on distance and your overall profile"). */
const PART_NAMES: Record<string, string> = {
  certifications: 'the preferred credentials',
  location: 'distance',
  experience: 'experience',
  skills: 'skills',
  profile: 'your overall profile',
};

/**
 * "What would raise it" for the overall-profile part of one job's score: credentials the readiness
 * score counts (reviewed and current), documents, work history, skills, photo, bio, the public switch
 * and a recent update. Each change is run through the real scoring for this job; only real gains are
 * listed, biggest first. When no single change moves the rounded score but all of them together
 * would, one combined item says so, so the list is never empty while something would raise it.
 */
function overallProfileImprovements(
  rp: ReadinessProfile,
  job: Job,
  already: ScoreImprovement[],
  score: number,
  sim: (c: ProfileChange) => number | null,
  opts: JobMatchOptions,
  now: Date,
): ScoreImprovement[] {
  const clock = scoringClock(rp, now);
  const readiness = calculateReadiness(rp, now);
  const certs = extractCertifications(rp);
  const listedCerts = new Set(already.map((i) => (i.cert ? certificationMatchKey(i.cert) : null)).filter(Boolean));
  const candidates: Array<{ id: string; change: ProfileChange; title: string; why: string; actions: ImprovementAction[]; cert?: string }> = [];

  // Credentials the readiness score counts.
  const emtState = normalizeStateCode(rp.address?.state ?? null);
  // ACLS and PALS are advanced-provider courses: not suggested to an EMT-Basic (no AEMT or Paramedic
  // credential on the profile) unless this job lists them (2026-10-14). The readiness score is unchanged.
  const advanced = isAdvancedProvider(certs);
  const jobReq = getJobCertRequirements(job as Parameters<typeof getJobCertRequirements>[0]);
  const jobNames = [...(jobReq.required ?? []), ...(jobReq.preferred ?? [])];
  for (const rc of READINESS_CREDENTIALS) {
    if (!advanced && ADVANCED_ONLY_COURSES.has(rc.name) && !jobNames.some((n) => certMatchesPattern(n, rc.patterns))) continue;
    const heldList = certs.filter((c) => certMatchesPattern(c.name, rc.patterns));
    if (heldList.some((c) => certCounts(c, clock))) continue;
    const h = heldList[0];
    const key = certificationMatchKey(h ? h.name : rc.name);
    if (key && listedCerts.has(key)) continue;
    if (h) {
      const expired = heldList.some((c) => isExpired(c.expiryDate, clock));
      const name = displayCertificationName(h.name);
      candidates.push({
        id: `profile:cert:${rc.label}`,
        change: heldChange({ name: h.name, state: h.state ?? null, level: h.level ?? null }),
        title: expired ? `Renew your ${name}` : `Get your ${name} document reviewed`,
        why: expired
          ? `It has expired, so it does not count toward your overall profile until a current card is uploaded and reviewed.`
          : `It is on your profile, but it counts toward your overall profile only once its document passes our document review.`,
        actions: certActions(name, job.id || '', true),
        cert: name,
      });
    } else {
      const isState = rc.name === STATE_EMS_CERTIFICATION;
      if (isState && !emtState) continue;
      const display = isState ? `${stateName(emtState)} EMS certification` : rc.label;
      candidates.push({
        id: `profile:cert:${rc.label}`,
        change: isState ? { kind: 'cert', name: STATE_EMS_CERTIFICATION, state: emtState } : { kind: 'cert', name: rc.name },
        title: isState ? `Add your ${display}` : rc.add,
        why: `It counts toward your overall profile once its document passes our document review.`,
        actions: certActions(isState ? formatCertificationName(STATE_EMS_CERTIFICATION, emtState) : rc.name, job.id || ''),
        cert: isState ? formatCertificationName(STATE_EMS_CERTIFICATION, emtState) : rc.name,
      });
    }
  }

  // Documents (the driver's license is the one required document that is not a credential).
  candidates.push({
    id: 'profile:document:driversLicense',
    change: { kind: 'document', category: 'driversLicense' },
    title: "Upload your driver's license",
    why: 'It is one of the four documents employers look for, and it counts once it passes our document review.',
    actions: [{ kind: 'upload', label: 'Upload documents', href: ACCOUNT_DOCUMENTS_HREF }],
  });

  // Work history: 5 or more years and 3 kinds of EMS work earn the whole Experience part.
  if (readiness.pillars.experience.score < 100) {
    const years = getYearsExperienceForReadiness(rp as never, scoringToday(clock));
    const past = (rp.pastExperience ?? []) as Array<{ serviceType?: string }>;
    const kindsHeld = VARIETY_KINDS.filter((k) => past.some((x) => k.test.test(x.serviceType || '')));
    const kindsToAdd = VARIETY_KINDS.filter((k) => !kindsHeld.includes(k)).slice(0, Math.max(0, 3 - kindsHeld.length));
    const target = Math.max(5, years);
    const clauses = [
      years < 5 ? '5 or more years of EMS work' : null,
      kindsToAdd.length ? `${kindsHeld.length + kindsToAdd.length} kinds of EMS work (for example ${joinWords(VARIETY_KINDS.map((k) => k.words))})` : null,
    ].filter(Boolean) as string[];
    if (clauses.length) {
      candidates.push({
        id: 'profile:experience',
        change: { kind: 'years', years: target, serviceTypes: kindsToAdd.map((k) => k.serviceType) },
        title: 'Add your EMS work history',
        why: `Your profile shows ${Math.round(years * 10) / 10} year${years === 1 ? '' : 's'}. If you have more than your profile shows, add it. This is what the score would be with ${joinWords(clauses)} on your profile.`,
        actions: [{ kind: 'edit', label: 'Update your experience', href: ACCOUNT_EXPERIENCE_HREF }],
      });
    }
  }

  // Skills: 15 or more, across at least 3 areas, earn the whole Skills part.
  if (readiness.pillars.skills.score < 100) {
    candidates.push({
      id: 'profile:skills',
      change: { kind: 'skill', skills: FULL_SKILL_SET },
      title: 'Add the skills you can do',
      why: 'This is what the score would be with 15 or more skills across at least 3 areas (for example airway, cardiac and trauma) on your profile. Add only what you can do.',
      actions: [{ kind: 'edit', label: 'Add skills', href: ACCOUNT_SKILLS_HREF }],
    });
  }

  if (!rp.photoURL) candidates.push({ id: 'profile:photo', change: { kind: 'photo' }, title: 'Add a profile photo', why: 'Part of your overall profile.', actions: [{ kind: 'edit', label: 'Add a photo', href: ACCOUNT_HREF }] });
  if ((rp.bio || '').trim().length <= 20) candidates.push({ id: 'profile:bio', change: { kind: 'bio' }, title: 'Write a short bio', why: 'Part of your overall profile.', actions: [{ kind: 'edit', label: 'Write a bio', href: ACCOUNT_BIO_HREF }] });
  if (rp.preferences?.publicProfile === false) candidates.push({ id: 'profile:public', change: { kind: 'public' }, title: 'Turn on your public profile', why: 'Part of your overall profile: employers can find you.', actions: [{ kind: 'edit', label: 'Profile settings', href: ACCOUNT_HREF }] });
  candidates.push({ id: 'profile:touch', change: { kind: 'touch' }, title: 'Review and save your profile', why: 'A profile counts as active when it was updated in the last 30 days.', actions: [{ kind: 'edit', label: 'Open your profile', href: ACCOUNT_HREF }] });

  const out: ScoreImprovement[] = [];
  for (const c of candidates) {
    const after = sim(c.change);
    if (after == null || after <= score) continue;
    out.push({
      id: c.id,
      kind: 'profile',
      title: c.title,
      detail: `${c.why} ${gainText(score, after)}`.trim(),
      newScore: after,
      gain: after - score,
      ...(c.cert ? { cert: c.cert } : {}),
      actions: c.actions,
      change: c.change,
    });
  }
  out.sort((a, b) => b.gain - a.gain);
  if (out.length > 0 || candidates.length === 0) return out;

  // No single change moves the rounded score: all of them together.
  let all = rp;
  for (const c of candidates) all = applyProfileChange(all, c.change, now);
  const together = calculateJobMatch(all, job, calculateReadiness(all, now), { ...opts, now }).total;
  if (together <= score) return [];
  return [{
    id: 'profile:all',
    kind: 'profile',
    title: 'Finish your profile',
    detail: `Each step on its own adds less than one point to this score: ${joinWords(candidates.map((c) => c.title.toLowerCase()))}. ${gainText(score, together)}`,
    newScore: together,
    gain: together - score,
    actions: [{ kind: 'edit', label: 'Open your profile', href: ACCOUNT_HREF }],
  }];
}

/* ------------------------------------------------------------------ overall */

export type CompetitivenessInput = {
  /** The board's population (loadCandidateJobs), already open and not test jobs. */
  jobs: readonly Job[];
  readinessProfile: ReadinessProfile | null;
  /** The private profile (for held credentials and the far-off rule). */
  profile: unknown;
  /** Per-job score, from the ONE evaluation (evaluateJobForCandidate(...).match.total). */
  scoreFor: (job: Job) => number | null;
  emtState: string | null;
  emtCoords: LatLng | null;
  maxTravelRadiusMiles: number | null;
  matchOptions?: JobMatchOptions;
  farOffOptions?: FarOffOptions;
  now?: Date;
};

export type OverallChange = {
  id: string;
  title: string;
  /** "Adding ACLS raises your score on 6 of 9 jobs near you." */
  detail: string;
  jobsRaised: number;
  poolSize: number;
  newAverage: number;
  gain: number;
  cert?: string;
  href: string;
  trainingHref?: string;
};

/**
 * Which jobs Competitiveness compares the EMT with, in words, and how to widen it. The average is
 * over open board jobs (not far off) in the EMT's address state, or within their saved travel
 * distance of their saved address. Before 2026-10-14 the card said only "Not enough jobs near you yet
 * to compare", even with many jobs on the board, and never said what "near" meant.
 */
export type CompetitivenessArea = {
  /** The EMT's address state (2-letter), null when there is none. */
  state: string | null;
  /** The saved travel distance in miles, null when none is saved. */
  travelMiles: number | null;
  /** A saved address map point exists (needed to measure a travel distance). */
  hasCoords: boolean;
  /** "in Utah or within your travel distance", "in Utah or within 50 miles of you", null when there is no rule yet. */
  phrase: string | null;
  /** Open board jobs (not far off) outside the area. */
  outside: number;
  /** The buttons the card shows. */
  actions: Array<{ id: 'travel' | 'address'; label: string; href: string }>;
};

export type CompetitivenessView =
  | { enough: false; poolSize: number; text: string; jobIds: string[]; area: CompetitivenessArea }
  | {
      enough: true;
      area: CompetitivenessArea;
      average: number;
      label: CompetitivenessLabel;
      tone: CompetitivenessTone;
      poolSize: number;
      jobIds: string[];
      perJob: Array<{ jobId: string; score: number }>;
      changes: OverallChange[];
    };

/** Rule 3 of the average: in the EMT's state, or within their saved travel distance. */
export function isJobNearEmt(job: Job, emtState: string | null, emtCoords: LatLng | null, maxTravelRadiusMiles: number | null, workCities: readonly LatLng[] = []): boolean {
  const state = normalizeStateCode(emtState);
  const js = jobStateCode(job);
  if (state && js && state === js) return true;
  const jc = getJobCoordinates(job);
  if (maxTravelRadiusMiles != null && maxTravelRadiusMiles > 0 && emtCoords) {
    if (jc && haversineMiles(emtCoords.lat, emtCoords.lng, jc.lat, jc.lng) <= maxTravelRadiusMiles) return true;
  }
  // 2026-10-15 (B4): within the travel distance (at least 25 miles) of one of the EMT's extra cities.
  if (jc && workCities.length) {
    const reach = Math.max(maxTravelRadiusMiles ?? 0, DISTANCE_FREE_MILES);
    if (workCities.some((c) => haversineMiles(c.lat, c.lng, jc.lat, jc.lng) <= reach)) return true;
  }
  return false;
}

/** The jobs the average is taken over (rules 1 to 3). */
export function competitivenessPool(input: Pick<CompetitivenessInput, 'jobs' | 'profile' | 'emtState' | 'emtCoords' | 'maxTravelRadiusMiles' | 'farOffOptions'>): Job[] {
  const held = getEmtCertificationEntries(input.profile as Parameters<typeof getEmtCertificationEntries>[0]);
  return input.jobs.filter(
    (j) => !isFarOffJob(j, held, input.farOffOptions) && isJobNearEmt(j, input.emtState, input.emtCoords, input.maxTravelRadiusMiles, emtWorkCityPoints(input.profile)),
  );
}

const avg = (xs: number[]) => Math.round(xs.reduce((s, x) => s + x, 0) / xs.length);

/** The area Competitiveness covers for this EMT, with the count of board jobs outside it. */
export function competitivenessArea(input: Pick<CompetitivenessInput, 'jobs' | 'profile' | 'emtState' | 'emtCoords' | 'maxTravelRadiusMiles' | 'farOffOptions'>, poolCount: number): CompetitivenessArea {
  const state = normalizeStateCode(input.emtState);
  const travelMiles = input.maxTravelRadiusMiles != null && input.maxTravelRadiusMiles > 0 ? input.maxTravelRadiusMiles : null;
  const hasCoords = Boolean(input.emtCoords);
  const held = getEmtCertificationEntries(input.profile as Parameters<typeof getEmtCertificationEntries>[0]);
  const visible = input.jobs.filter((j) => !isFarOffJob(j, held, input.farOffOptions)).length;
  const distance = travelMiles && hasCoords ? `within ${travelMiles} miles of you` : null;
  const cityCount = emtWorkCityPoints(input.profile).length;
  const cities = cityCount ? `, or near the ${cityCount === 1 ? 'city' : `${cityCount} cities`} you added` : '';
  const phrase = state
    ? `in ${stateName(state)} or ${distance ?? 'within your travel distance'}${cities}`
    : distance ? `${distance}${cities}` : cities ? cities.replace(/^, or /, '') : distance;
  const actions: CompetitivenessArea['actions'] = [
    { id: 'travel', label: travelMiles ? 'Change your travel distance' : 'Set a travel distance', href: JOB_PREFERENCES_HREF },
  ];
  if (!state || (travelMiles && !hasCoords)) actions.push({ id: 'address', label: 'Add your address', href: ADDRESS_HREF });
  return { state, travelMiles, hasCoords, phrase, outside: Math.max(0, visible - poolCount), actions };
}

/** The not-enough line: the rule, how many board jobs are outside it, and what widens it. */
export function competitivenessRuleText(area: CompetitivenessArea, poolSize: number): string {
  if (!area.phrase) {
    return "We compare you with jobs in your state or within your travel distance, and your profile has neither yet. Add your address or set a travel distance to see how you compare.";
  }
  const parts = [`We compare you with jobs ${area.phrase}. ${countLabel(area.outside, 'job')} on the board ${area.outside === 1 ? 'is' : 'are'} outside that.`];
  parts.push(poolSize > 0 ? `${countLabel(poolSize, 'job')} ${poolSize === 1 ? 'is' : 'are'} inside it; it takes ${MIN_JOBS_FOR_AVERAGE} to compare.` : `It takes ${MIN_JOBS_FOR_AVERAGE} jobs inside it to compare.`);
  if (!area.travelMiles) parts.push("You haven't set a travel distance yet.");
  else if (!area.hasCoords) parts.push('Add your address so we can measure your travel distance.');
  return parts.join(' ');
}

export function competitivenessFromJobs(input: CompetitivenessInput): CompetitivenessView {
  const now = input.now ?? new Date();
  const pool = competitivenessPool(input);
  const scored = pool.map((job) => ({ job, score: input.scoreFor(job) })).filter((x): x is { job: Job; score: number } => x.score != null);
  const jobIds = scored.map((x) => x.job.id || '');
  const area = competitivenessArea(input, pool.length);
  if (scored.length < MIN_JOBS_FOR_AVERAGE) return { enough: false, poolSize: scored.length, text: competitivenessRuleText(area, scored.length), jobIds, area };
  const average = avg(scored.map((x) => x.score));
  const { label, tone } = competitivenessLabel(average);
  return {
    enough: true,
    area,
    average,
    label,
    tone,
    poolSize: scored.length,
    jobIds,
    perJob: scored.map((x) => ({ jobId: x.job.id || '', score: x.score })),
    changes: input.readinessProfile ? overallChanges(scored, input.readinessProfile, input.matchOptions ?? {}, now) : [],
  };
}

/**
 * The changes that would raise the average most across the pool: every required or preferred
 * credential a pooled job lists that the EMT does not hold, the skills those jobs list, a profile
 * photo and a bio. Each change is simulated on every pooled job; ranked by how much it raises the
 * average, then by how many jobs it raises.
 */
export function overallChanges(
  scored: ReadonlyArray<{ job: Job; score: number }>,
  rp: ReadinessProfile,
  opts: JobMatchOptions,
  now: Date,
): OverallChange[] {
  const candidates = new Map<string, { change: ProfileChange; title: string; verb: string; cert?: string; href: string; trainingHref?: string }>();
  const held = getEmtCertificationEntries({ professional: rp.professional } as never);
  // Only credentials that COUNT (reviewed and current) are skipped: one on the profile that does not
  // count yet is offered as "get it reviewed" (or "renew it"), with its real gain.
  const counting = countingCertificationEntries(rp, scoringClock(rp, now));
  for (const { job } of scored) {
    const { required, preferred } = getJobCertRequirements(job as Parameters<typeof getJobCertRequirements>[0]);
    const ctx = certificationJobContext(job);
    for (const c of [...required, ...preferred]) {
      if (counting.some((h) => certificationSatisfiesForJob(h.name, h.state, c, ctx, h.level))) continue;
      const canonical = resolveCanonicalCertification(c);
      // A State EMS Certification is per state: "for FL" when the pool's jobs name one.
      const name = canonical === STATE_EMS_CERTIFICATION ? formatCertificationName(STATE_EMS_CERTIFICATION, jobStateCode(job)) : displayCertificationName(c);
      const heldEntry = held.find((h) => certificationSatisfiesForJob(h.name, h.state, c, ctx, h.level));
      const key = `cert:${canonical === STATE_EMS_CERTIFICATION ? `${certificationMatchKey(c)}:${jobStateCode(job) ?? ''}` : certificationMatchKey(c)}`;
      if (!candidates.has(key)) {
        candidates.set(
          key,
          heldEntry
            ? { change: heldChange(heldEntry), title: `Get your ${name} document reviewed`, verb: `Getting your ${name} document reviewed`, cert: name, href: buildStrengthenAccountPath([name]) }
            : { change: certChange(c, job), title: `Add ${name}`, verb: `Adding ${name}`, cert: name, href: buildStrengthenAccountPath([name]), trainingHref: findTrainingPath(name) },
        );
      }
    }
  }
  const missingSkills = new Set<string>();
  for (const { job } of scored) {
    const m = calculateJobMatch(rp, job, calculateReadiness(rp, now), { ...opts, now }).pillars.skills;
    if (m.applicable) m.missing.forEach((s) => missingSkills.add(s));
  }
  for (const s of missingSkills) candidates.set(`skill:${s.toLowerCase()}`, { change: { kind: 'skill', skills: [s] }, title: `Add the skill ${s}`, verb: `Adding the skill ${s}`, href: ACCOUNT_SKILLS_HREF });
  if (!rp.photoURL) candidates.set('photo', { change: { kind: 'photo' }, title: 'Add a profile photo', verb: 'Adding a profile photo', href: ACCOUNT_HREF });
  if ((rp.bio || '').trim().length <= 20) candidates.set('bio', { change: { kind: 'bio' }, title: 'Write a short bio', verb: 'Writing a short bio', href: ACCOUNT_BIO_HREF });

  const before = scored.map((x) => x.score);
  const baseAvg = avg(before);
  const out: OverallChange[] = [];
  for (const [id, c] of candidates) {
    const next = applyProfileChange(rp, c.change, now);
    const nextReadiness = calculateReadiness(next, now);
    const after = scored.map(({ job }) => calculateJobMatch(next, job, nextReadiness, { ...opts, now }).total);
    const jobsRaised = after.filter((a, i) => a > before[i]).length;
    if (jobsRaised === 0) continue;
    const newAverage = avg(after);
    const exactGain = after.reduce((s, a, i) => s + (a - before[i]), 0) / scored.length;
    out.push({
      id,
      title: c.title,
      detail: `${c.verb} raises your score on ${jobsRaised} of ${countLabel(scored.length, 'job')} near you${newAverage > baseAvg ? ` (your average goes from ${baseAvg}% to ${newAverage}%)` : ''}.`,
      jobsRaised,
      poolSize: scored.length,
      newAverage,
      gain: Math.round(exactGain * 10) / 10,
      cert: c.cert,
      href: c.href,
      trainingHref: c.trainingHref,
    });
  }
  return out.sort((a, b) => b.gain - a.gain || b.jobsRaised - a.jobsRaised).slice(0, 5);
}
